import React, {useEffect, useState} from 'react';
import styles from '../tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";

const AdminInviteCodesTable = () => {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createError, setCreateError] = useState('');
    const [createdInvite, setCreatedInvite] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [pendingDeactivateId, setPendingDeactivateId] = useState(null);
    const [deactivateError, setDeactivateError] = useState('');
    const [isDeactivating, setIsDeactivating] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 20;

    const fetchCodes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("prismax_admin_token");
            const headers = token ? {"Authorization": `Bearer ${token}`} : {};
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/get-admin-invite-codes`, {headers});
            if (!response.ok) {
                throw new Error('Failed to fetch invite codes');
            }
            const data = await response.json();
            if (data.success && data.data) {
                setCodes(data.data);
                const nextTotalPages = Math.max(1, Math.ceil(data.data.length / itemsPerPage));
                setTotalPages(nextTotalPages);
                setCurrentPage((prev) => Math.min(prev, nextTotalPages));
            } else {
                throw new Error('Invalid data format');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCodes();
    }, []);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = codes.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleCreateInvite = async () => {
        setCreateError('');
        setIsCreating(true);
        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/create-admin-invite-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({})
            });

            const data = await response.json();
            if (data.success && data.data) {
                setCreatedInvite(data.data);
                await fetchCodes();
            } else {
                setCreateError(data.msg || 'Failed to create invite code');
            }
        } catch (err) {
            setCreateError('Failed to create invite code: ' + err.message);
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeactivateInvite = async () => {
        if (!pendingDeactivateId) {
            return;
        }
        setDeactivateError('');
        setIsDeactivating(true);
        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/deactivate-admin-invite-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({invite_id: pendingDeactivateId})
            });

            const data = await response.json();
            if (data.success) {
                setShowDeactivateModal(false);
                setPendingDeactivateId(null);
                await fetchCodes();
            } else {
                setDeactivateError(data.msg || 'Failed to deactivate invite code');
            }
        } catch (err) {
            setDeactivateError('Failed to deactivate invite code: ' + err.message);
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleCopy = async () => {
        if (!createdInvite?.code) {
            return;
        }
        try {
            await navigator.clipboard.writeText(createdInvite.code);
        } catch (err) {
            setCreateError('Failed to copy code');
        }
    };

    const formatDateTime = (value) => {
        if (!value) {
            return '--';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString();
    };

    if (loading) {
        return <div className={styles.loading}>Loading invite codes...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>Admin Invite Codes</h2>
                <button
                    className={styles.addButton}
                    onClick={() => {
                        setShowCreateModal(true);
                        setCreateError('');
                        setCreatedInvite(null);
                    }}
                >
                    Create Invite Code
                </button>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Invite ID</th>
                        <th>Code</th>
                        <th>Invited User ID</th>
                        <th>Creator Admin ID</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentItems.length > 0 ? (
                        currentItems.map((invite) => (
                            <tr key={invite.invite_id}>
                                <td>{invite.invite_id}</td>
                                <td>{invite.code}</td>
                                <td>{invite.invited_user_id ?? '--'}</td>
                                <td>{invite.creator_admin_id}</td>
                                <td>{formatDateTime(invite.created_at)}</td>
                                <td>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={() => {
                                            setPendingDeactivateId(invite.invite_id);
                                            setDeactivateError('');
                                            setShowDeactivateModal(true);
                                        }}
                                    >
                                        Deactivate
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className={styles.noData}>
                                No invite codes found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {codes.length > itemsPerPage && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>
                </div>
            )}

            {showCreateModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Create Invite Code</h3>

                        <div className={styles.formGroup}>
                            <label>New Code</label>
                            <input
                                type="text"
                                value={createdInvite?.code || ''}
                                placeholder="Click Create to generate"
                                readOnly
                                className={styles.input}
                            />
                        </div>

                        {createError && (
                            <div className={styles.errorMessage}>{createError}</div>
                        )}

                        <div className={styles.modalButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setCreateError('');
                                    setCreatedInvite(null);
                                }}
                            >
                                Close
                            </button>
                            <button
                                className={styles.cancelButton}
                                onClick={handleCopy}
                                disabled={!createdInvite?.code}
                            >
                                Copy
                            </button>
                            <button
                                className={styles.confirmButton}
                                onClick={handleCreateInvite}
                                disabled={isCreating}
                            >
                                {isCreating ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeactivateModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Deactivate Invite Code</h3>

                        <p className={styles.modalDescription}>
                            Are you sure you want to deactivate this invite code? It will no longer be usable.
                        </p>

                        {deactivateError && (
                            <div className={styles.errorMessage}>{deactivateError}</div>
                        )}

                        <div className={styles.modalButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={() => {
                                    setShowDeactivateModal(false);
                                    setPendingDeactivateId(null);
                                    setDeactivateError('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.confirmButton}
                                onClick={handleDeactivateInvite}
                                disabled={isDeactivating}
                            >
                                {isDeactivating ? 'Deactivating...' : 'Deactivate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminInviteCodesTable;
